import React, { useCallback, type PointerEvent as ReactPointerEvent } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { coverEditorMessages } from '../coverEditorMessages';
import type { BoxGesture } from './useBoxPointer';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SelectionFrameProps {
  box: CoverTextBox;
  onStart: (event: ReactPointerEvent<HTMLElement>, box: CoverTextBox, gesture: BoxGesture) => void;
  onMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onEnd: (event: ReactPointerEvent<HTMLElement>) => void;
}

const CORNERS = ['handleTopLeft', 'handleTopRight', 'handleBottomLeft', 'handleBottomRight'] as const;
const SIDES = ['handleLeft', 'handleRight'] as const;

/**
 * The frame of the selected text box (design cover-editor b1): four corner handles size the text,
 * two side handles set the wrap width, the round handle turns the box.
 */
const SelectionFrameComponent: React.FC<SelectionFrameProps> = ({ box, onStart, onMove, onEnd }) => {
  const intl = useIntl();
  const startCorner = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => onStart(event, box, 'corner'),
    [box, onStart]
  );
  const startSide = useCallback((event: ReactPointerEvent<HTMLElement>) => onStart(event, box, 'side'), [box, onStart]);
  const startRotate = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => onStart(event, box, 'rotate'),
    [box, onStart]
  );

  return (
    <span className={styles.selection}>
      <span className={styles.stem} />
      <span
        className={clsx(styles.handle, styles.handleRotate)}
        role="slider"
        tabIndex={-1}
        aria-label={intl.formatMessage(coverEditorMessages.handleRotate)}
        aria-valuenow={Math.round(box.rotationDeg)}
        aria-valuemin={-180}
        aria-valuemax={180}
        onPointerDown={startRotate}
        onPointerMove={onMove}
        onPointerUp={onEnd}
        onPointerCancel={onEnd}
      />
      {CORNERS.map((corner) => (
        <span
          key={corner}
          className={clsx(styles.handle, styles[corner])}
          aria-hidden
          onPointerDown={startCorner}
          onPointerMove={onMove}
          onPointerUp={onEnd}
          onPointerCancel={onEnd}
        />
      ))}
      {SIDES.map((side) => (
        <span
          key={side}
          className={clsx(styles.handle, styles[side])}
          aria-hidden
          onPointerDown={startSide}
          onPointerMove={onMove}
          onPointerUp={onEnd}
          onPointerCancel={onEnd}
        />
      ))}
    </span>
  );
};

export const SelectionFrame = TypedMemo(SelectionFrameComponent);
